import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuthContext } from "@/contexts/AuthContext";

interface OnboardingState {
  hasCompletedOnboarding: boolean;
  hasCreatedFirstOrder: boolean;
  hasSeenSmartInput: boolean;
  hasSeenFeatureComparison: boolean;
  lastSeenVersion: string;
  hasMarkedOrderReady: boolean;
  hasViewedArchive: boolean;
  hasViewedDashboard: boolean;
  checklistDismissed: boolean;
}

interface OnboardingContextValue {
  state: OnboardingState;
  markOnboardingComplete: () => void;
  markFirstOrderCreated: () => void;
  markSmartInputSeen: () => void;
  markFeatureComparisonSeen: () => void;
  markOrderReady: () => void;
  markArchiveViewed: () => void;
  markDashboardViewed: () => void;
  dismissChecklist: () => void;
  resetOnboarding: () => void;
  checklistItems: { key: string; completed: boolean }[];
  completedCount: number;
  totalCount: number;
  isChecklistComplete: boolean;
  showOnboarding: boolean;
  setShowOnboarding: (show: boolean) => void;
}

const GENERIC_KEY = "orderflow-onboarding";
const CURRENT_VERSION = "1.2.0";

const defaultState: OnboardingState = {
  hasCompletedOnboarding: false,
  hasCreatedFirstOrder: false,
  hasSeenSmartInput: false,
  hasSeenFeatureComparison: false,
  lastSeenVersion: CURRENT_VERSION,
  hasMarkedOrderReady: false,
  hasViewedArchive: false,
  hasViewedDashboard: false,
  checklistDismissed: false,
};

function getStorageKey(userId: string | undefined) {
  return userId ? `${GENERIC_KEY}-${userId}` : GENERIC_KEY;
}

function loadState(userId: string | undefined): OnboardingState {
  try {
    const scopedKey = getStorageKey(userId);
    const scoped = localStorage.getItem(scopedKey);
    if (scoped) return { ...defaultState, ...JSON.parse(scoped) };

    // Migration: if user-scoped key doesn't exist but generic does, migrate
    if (userId) {
      const generic = localStorage.getItem(GENERIC_KEY);
      if (generic) {
        const parsed = { ...defaultState, ...JSON.parse(generic) };
        localStorage.setItem(scopedKey, JSON.stringify(parsed));
        localStorage.removeItem(GENERIC_KEY);
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return defaultState;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const userId = user?.id;

  const [state, setState] = useState<OnboardingState>(() => loadState(userId));
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Re-load state when userId changes (login/logout)
  useEffect(() => {
    setState(loadState(userId));
  }, [userId]);

  // Persist to scoped key
  useEffect(() => {
    const key = getStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(state));
  }, [state, userId]);

  const markOnboardingComplete = useCallback(() => {
    setState((prev) => ({ ...prev, hasCompletedOnboarding: true }));
  }, []);

  const markFirstOrderCreated = useCallback(() => {
    setState((prev) => ({ ...prev, hasCreatedFirstOrder: true }));
  }, []);

  const markSmartInputSeen = useCallback(() => {
    setState((prev) => ({ ...prev, hasSeenSmartInput: true }));
  }, []);

  const markFeatureComparisonSeen = useCallback(() => {
    setState((prev) => ({ ...prev, hasSeenFeatureComparison: true }));
  }, []);

  const markOrderReady = useCallback(() => {
    setState((prev) => ({ ...prev, hasMarkedOrderReady: true }));
  }, []);

  const markArchiveViewed = useCallback(() => {
    setState((prev) => ({ ...prev, hasViewedArchive: true }));
  }, []);

  const markDashboardViewed = useCallback(() => {
    setState((prev) => ({ ...prev, hasViewedDashboard: true }));
  }, []);

  const dismissChecklist = useCallback(() => {
    setState((prev) => ({ ...prev, checklistDismissed: true }));
  }, []);

  const resetOnboarding = useCallback(() => {
    setState(defaultState);
  }, []);

  const checklistItems = [
    { key: "hasCreatedFirstOrder", completed: state.hasCreatedFirstOrder },
    { key: "hasMarkedOrderReady", completed: state.hasMarkedOrderReady },
    { key: "hasViewedArchive", completed: state.hasViewedArchive },
    { key: "hasViewedDashboard", completed: state.hasViewedDashboard },
  ];

  const completedCount = checklistItems.filter((item) => item.completed).length;
  const totalCount = checklistItems.length;
  const isChecklistComplete = completedCount === totalCount;

  const contextValue: OnboardingContextValue = {
    state,
    markOnboardingComplete,
    markFirstOrderCreated,
    markSmartInputSeen,
    markFeatureComparisonSeen,
    markOrderReady,
    markArchiveViewed,
    markDashboardViewed,
    dismissChecklist,
    resetOnboarding,
    checklistItems,
    completedCount,
    totalCount,
    isChecklistComplete,
    showOnboarding,
    setShowOnboarding,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
}
